import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, MapPin, Calendar, ExternalLink, Tag, Lock, Building, Loader2, ArrowLeft,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';

const CITIES = [
  'Los Angeles', 'Porter Ranch', 'San Diego', 'San Francisco', 'Bay Area', 'Sacramento',
  'Orange County', 'San Jose', 'Santa Barbara', 'Palm Springs', 'Other California',
];

// Match wellness events by keyword in title or description (no schema migration needed)
const WELLNESS_KEYWORDS = [
  'wellness', 'yoga', 'meditation', 'breathwork', 'mindful', 'mindfulness',
  'spa', 'massage', 'prenatal', 'postnatal', 'postpartum', 'self-care', 'self care',
  'pilates', 'fitness', 'sound bath', 'sound healing', 'reiki', 'therapy', 'nutrition',
  'doula', 'lactation', 'pelvic floor',
];

const PAID_TIERS = new Set(['thrive', 'premium', 'connected']);

const isPaidMember = (u) => {
  if (!u) return false;
  if (u.is_admin || u.is_founder_account) return true;
  const tier = (u.membership_tier || '').toLowerCase();
  if (!PAID_TIERS.has(tier)) return false;
  if (u.membership_paused) return false;
  return true;
};

const isWellnessEvent = (e) => {
  const blob = `${e.title || ''} ${e.description || ''} ${e.event_type || ''}`.toLowerCase();
  return WELLNESS_KEYWORDS.some((kw) => blob.includes(kw));
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const TopNav = () => (
  <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/dashboard" className="font-display text-xl text-navy">
        Cali<span className="italic-teal">Moms</span> Connect
      </Link>
      <nav className="hidden md:flex items-center gap-6">
        <Link to="/dashboard" className="text-sm text-text-mid hover:text-teal">Dashboard</Link>
        <Link to="/my-city" className="text-sm text-text-mid hover:text-teal">My City</Link>
        <Link to="/community" className="text-sm text-text-mid hover:text-teal">Community Board</Link>
        <span className="text-sm text-teal font-semibold" data-testid="wellness-nav-active">Wellness</span>
      </nav>
    </div>
  </header>
);

const PaywallCard = () => (
  <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-card p-10 text-center border border-seafoam/40" data-testid="wellness-paywall">
    <div className="w-16 h-16 mx-auto rounded-full bg-teal/10 flex items-center justify-center mb-5">
      <Lock className="text-teal" size={28} />
    </div>
    <h2 className="font-display text-3xl text-navy mb-3">Wellness is a member benefit</h2>
    <p className="text-text-mid mb-6 max-w-md mx-auto">
      Curated wellness providers, member-only wellness events, and listings from local moms — available to
      Connect and Thrive members (or anyone on a paid promo).
    </p>
    <Link to="/pricing">
      <Button className="bg-teal hover:bg-teal-deep text-white rounded-full px-8" data-testid="wellness-upgrade-cta">
        See Membership Plans
      </Button>
    </Link>
  </div>
);

const ProviderCard = ({ b }) => (
  <div className="bg-white rounded-2xl shadow-card p-5 flex flex-col gap-3 hover:shadow-lg transition-shadow" data-testid={`wellness-provider-${b.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="font-semibold text-navy text-lg leading-tight">{b.business_name || b.name}</h3>
        <p className="text-xs text-text-mid mt-1 flex items-center gap-1"><MapPin size={12} /> {b.city || '—'}</p>
      </div>
      {b.is_featured && <Badge className="bg-amber-100 text-amber-800 shrink-0">Featured</Badge>}
    </div>
    {b.description && <p className="text-sm text-text-mid line-clamp-3">{b.description}</p>}
    {b.member_discount && (
      <div className="text-xs bg-teal/5 border border-teal/20 rounded-lg px-3 py-2 text-teal-deep flex items-center gap-1">
        <Tag size={12} /> {b.member_discount}
      </div>
    )}
    <div className="flex items-center gap-2 mt-auto">
      {b.website && (
        <a href={b.website} target="_blank" rel="noreferrer" className="text-sm text-teal hover:underline flex items-center gap-1">
          Visit <ExternalLink size={12} />
        </a>
      )}
    </div>
  </div>
);

const EventCard = ({ e }) => (
  <Link to={`/events/${e.id}`} className="block bg-white rounded-2xl shadow-card p-5 hover:shadow-lg transition-shadow" data-testid={`wellness-event-${e.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-navy text-lg leading-tight truncate">{e.title}</h3>
        <p className="text-xs text-text-mid mt-1 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(e.event_date)}</span>
          {e.city && <span className="flex items-center gap-1"><MapPin size={12} /> {e.city}</span>}
        </p>
      </div>
      {e.is_paid && <Badge className="bg-amber-100 text-amber-800 shrink-0">${e.price}</Badge>}
    </div>
    {e.description && <p className="text-sm text-text-mid mt-3 line-clamp-2">{e.description}</p>}
  </Link>
);

const ListingCard = ({ l }) => (
  <div className="bg-white rounded-2xl shadow-card p-5" data-testid={`wellness-listing-${l.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-navy leading-tight">{l.title}</h3>
        <p className="text-xs text-text-mid mt-1 flex items-center gap-1"><MapPin size={12} /> {l.city || '—'}</p>
      </div>
      {l.price != null && l.price !== '' && (
        <Badge className="bg-teal/10 text-teal-deep shrink-0">${l.price}</Badge>
      )}
    </div>
    {l.description && <p className="text-sm text-text-mid mt-2 line-clamp-3">{l.description}</p>}
    {l.poster?.first_name && <p className="text-xs text-text-light mt-3">Posted by {l.poster.first_name}</p>}
  </div>
);

export default function WellnessPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const paid = isPaidMember(user);

  const [cityFilter, setCityFilter] = useState(user?.city || 'all_cities');
  const [activeTab, setActiveTab] = useState('providers');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [events, setEvents] = useState([]);
  const [listings, setListings] = useState([]);

  const fetchAll = useCallback(async () => {
    if (!paid) return;
    setLoading(true);
    try {
      const [bRes, eRes, lParams] = [
        api.get('/businesses'),
        api.get('/events'),
        new URLSearchParams({ category: 'wellness' }),
      ];
      const [bResp, eResp] = await Promise.all([bRes, eRes]);
      if (cityFilter && cityFilter !== 'all_cities') lParams.set('city', cityFilter);
      const lResp = await api.get(`/marketplace/listings?${lParams.toString()}`);

      let bs = (bResp.data?.businesses || []).filter((b) => b.category === 'wellness');
      if (cityFilter && cityFilter !== 'all_cities') bs = bs.filter((b) => b.city === cityFilter);
      setProviders(bs);

      let es = (eResp.data || []).filter(isWellnessEvent);
      if (cityFilter && cityFilter !== 'all_cities') es = es.filter((e) => e.city === cityFilter);
      // Only show upcoming
      const now = Date.now();
      es = es.filter((e) => !e.event_date || new Date(e.event_date).getTime() >= now - 86400000);
      es.sort((a, b) => new Date(a.event_date || 0) - new Date(b.event_date || 0));
      setEvents(es);

      setListings(lResp.data?.listings || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load wellness content');
    } finally {
      setLoading(false);
    }
  }, [paid, cityFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const counts = useMemo(() => ({
    providers: providers.length,
    events: events.length,
    listings: listings.length,
  }), [providers, events, listings]);

  if (!paid) {
    return (
      <div className="min-h-screen bg-cream font-body">
        <TopNav />
        <div className="max-w-7xl mx-auto px-6 py-16">
          <PaywallCard />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream font-body">
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-white border border-border-subtle flex items-center justify-center text-navy hover:bg-seafoam/30 hover:text-teal transition-colors shadow-sm shrink-0"
              aria-label="Back"
              data-testid="wellness-back-btn"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center">
              <Sparkles className="text-teal" size={22} />
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-navy" data-testid="wellness-page-title">Wellness</h1>
              <p className="text-text-mid text-sm mt-1">Trusted providers, member events, and listings from moms in your city.</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-text-mid font-medium hidden sm:inline">City</span>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-52 rounded-xl border-seafoam bg-white" data-testid="wellness-city-select">
                <SelectValue placeholder="Select a city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_cities">All Cities</SelectItem>
                {CITIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="wellness-tabs">
          <TabsList className="bg-white rounded-full p-1 mb-6">
            <TabsTrigger value="providers" className="rounded-full px-5" data-testid="wellness-tab-providers">
              <Building size={14} className="mr-1.5" /> Providers ({counts.providers})
            </TabsTrigger>
            <TabsTrigger value="events" className="rounded-full px-5" data-testid="wellness-tab-events">
              <Calendar size={14} className="mr-1.5" /> Events ({counts.events})
            </TabsTrigger>
            <TabsTrigger value="listings" className="rounded-full px-5" data-testid="wellness-tab-listings">
              <Tag size={14} className="mr-1.5" /> Community Posts ({counts.listings})
            </TabsTrigger>
          </TabsList>

          {loading && (
            <div className="text-center py-12 text-text-mid"><Loader2 className="animate-spin mx-auto text-teal mb-2" size={24} />Loading wellness content…</div>
          )}

          {!loading && (
            <>
              <TabsContent value="providers">
                {providers.length === 0 ? (
                  <div className="bg-white rounded-2xl p-10 text-center text-text-mid" data-testid="wellness-empty-providers">
                    No wellness providers in {cityFilter === 'all_cities' ? 'any city' : cityFilter} yet.
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {providers.map((b) => <ProviderCard key={b.id} b={b} />)}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="events">
                {events.length === 0 ? (
                  <div className="bg-white rounded-2xl p-10 text-center text-text-mid" data-testid="wellness-empty-events">
                    No upcoming wellness events in {cityFilter === 'all_cities' ? 'any city' : cityFilter}.
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {events.map((e) => <EventCard key={e.id} e={e} />)}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="listings">
                {listings.length === 0 ? (
                  <div className="bg-white rounded-2xl p-10 text-center text-text-mid" data-testid="wellness-empty-listings">
                    No wellness listings from moms in {cityFilter === 'all_cities' ? 'any city' : cityFilter} yet.
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {listings.map((l) => <ListingCard key={l.id} l={l} />)}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
}
